export const formatKilos = (value) =>
  new Intl.NumberFormat('es-ES', {
    maximumFractionDigits: 0,
  }).format(value);

export const formatPercent = (value) =>
  new Intl.NumberFormat('es-ES', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

export const calculateFlowTotals = (rows) => {
  const totals = rows.reduce(
    (acc, row) => ({
      kilosVolcados: acc.kilosVolcados + row.kilosVolcados,
      kilosClasificados: acc.kilosClasificados + row.kilosClasificados,
      otrosCalibres: acc.otrosCalibres + row.otrosCalibres,
      retorno: acc.retorno + row.retorno,
      verde: acc.verde + row.verde,
      bajoCalibre: acc.bajoCalibre + row.bajoCalibre,
      industria: acc.industria + row.industria,
      podrido: acc.podrido + row.podrido,
      segunda: acc.segunda + row.segunda,
      totalPalots: acc.totalPalots + row.totalPalots,
    }),
    {
      kilosVolcados: 0,
      kilosClasificados: 0,
      otrosCalibres: 0,
      retorno: 0,
      verde: 0,
      bajoCalibre: 0,
      industria: 0,
      podrido: 0,
      segunda: 0,
      totalPalots: 0,
    }
  );

  totals.kilosSinClasificar = Math.max(totals.kilosVolcados - totals.kilosClasificados, 0);
  totals.rendimiento = totals.kilosVolcados > 0
    ? (totals.kilosClasificados * 100) / totals.kilosVolcados
    : 0;

  return totals;
};

export const groupTotalsByVariety = (rows) =>
  rows.reduce((acc, row) => {
    const varietyKey = row.idVariedad ?? row.variedad;
    const current = acc[varietyKey] || {
      idVariedad: row.idVariedad,
      variedad: row.variedad,
      kilosVolcados: 0,
      kilosClasificados: 0,
      industria: 0,
      podrido: 0,
      registros: 0,
    };

    current.kilosVolcados += row.kilosVolcados;
    current.kilosClasificados += row.kilosClasificados;
    current.industria += row.industria;
    current.podrido += row.podrido;
    current.registros += 1;

    acc[varietyKey] = current;
    return acc;
  }, {});

export const groupTotalsByVarietyAndWarehouse = (rows, warehouses = []) =>
  rows.reduce((acc, row) => {
    const warehouse = warehouses.find((item) => item.id === row.idAlmacen);
    const varietyKey = row.idVariedad ?? row.variedad;
    const warehouseKey = row.idAlmacen ?? 'sin-almacen';
    const key = `${varietyKey}-${warehouseKey}`;
    const current = acc[key] || {
      idVariedad: row.idVariedad,
      idAlmacen: row.idAlmacen,
      variedad: row.variedad,
      almacen: warehouse?.nombre || 'Sin almacen',
      kilosVolcados: 0,
      kilosClasificados: 0,
      industria: 0,
      podrido: 0,
      registros: 0,
    };

    current.kilosVolcados += row.kilosVolcados;
    current.kilosClasificados += row.kilosClasificados;
    current.industria += row.industria;
    current.podrido += row.podrido;
    current.registros += 1;

    acc[key] = current;
    return acc;
  }, {});

export const groupTotalsByWarehouse = (rows, warehouses = []) =>
  warehouses.map((warehouse) => {
    const warehouseRows = rows.filter((row) => row.idAlmacen === warehouse.id);
    const totals = calculateFlowTotals(warehouseRows);
    const variedades = new Set(
      warehouseRows.map((row) => row.idVariedad ?? row.variedad).filter(Boolean)
    );

    return {
      idAlmacen: warehouse.id,
      almacen: warehouse.nombre,
      registros: warehouseRows.length,
      variedades: variedades.size,
      ...totals,
    };
  });

export const groupFieldByWarehouse = (rows, warehouses = [], field) =>
  warehouses.map((warehouse) => ({
    idAlmacen: warehouse.id,
    almacen: warehouse.nombre,
    value: rows
      .filter((row) => row.idAlmacen === warehouse.id)
      .reduce((acc, row) => acc + toNumber(row[field]), 0),
  }));

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const calculatePaletsEntrada = (paletsEntrada = {}, cajasPalet = []) =>
  cajasPalet.reduce((acc, cajaPalet) => {
    const cantidad = toNumber(paletsEntrada[cajaPalet.id]);

    return {
      kilos: acc.kilos + cantidad * toNumber(cajaPalet.cajas),
      palets: acc.palets + cantidad,
    };
  }, { kilos: 0, palets: 0 });

const percentage = (value, total) => (total > 0 ? (value * 100) / total : 0);

export const calculateKgVolcados = (entrada, cajasPalet = []) => {
  const kilos = toNumber(entrada.kilos);
  const crucesPalots = toNumber(entrada.crucesPalots);
  const kilosPalot = toNumber(entrada.kilosPalot);
  const pesoCaja = toNumber(entrada.pesoCaja);
  const paletsEntrada = calculatePaletsEntrada(entrada.paletsEntrada, cajasPalet);

  return {
    kilosVolcados:
      kilos +
      crucesPalots * 4 * kilosPalot +
      paletsEntrada.kilos * pesoCaja,
    paletsEntrada: paletsEntrada.palets,
  };
};

export const calculateEntradaTotals = (entrada, cajasPalet = [], options = {}) => {
  const kilosPalot = toNumber(entrada.kilosPalot);
  const pesoPalotMerma = toNumber(options.pesoPalotMerma ?? entrada.pesoPalotMerma ?? 183);
  const { kilosVolcados, paletsEntrada } = calculateKgVolcados(entrada, cajasPalet);

  const retornoKg = toNumber(entrada.retorno) + toNumber(entrada.pRetorno) * kilosPalot;
  const otrosCalibresKg =
    toNumber(entrada.otrosCalibres) + toNumber(entrada.pOtrosCalibres) * kilosPalot;
  const verdeKgParaReales = toNumber(entrada.verde) + toNumber(entrada.pVerde) * kilosPalot;
  const verdeKg = toNumber(entrada.verde) + toNumber(entrada.pVerde) * pesoPalotMerma;

  const kgReales = kilosVolcados - (retornoKg + verdeKgParaReales + otrosCalibresKg);

  const segundaKg = toNumber(entrada.segunda) + toNumber(entrada.pSegunda) * pesoPalotMerma;
  const bajoCalibreKg =
    toNumber(entrada.bajoCalibre) + toNumber(entrada.pBajoCalibre) * pesoPalotMerma;
  const industriaKg = toNumber(entrada.industria) + toNumber(entrada.pIndustria) * pesoPalotMerma;
  const podridoKg = toNumber(entrada.podrido);

  const percentages = {
    retorno: percentage(retornoKg, kilosVolcados),
    otrosCalibres: percentage(otrosCalibresKg, kilosVolcados),
    verde: percentage(verdeKg, kilosVolcados),
    segunda: percentage(segundaKg, kgReales),
    bajoCalibre: percentage(bajoCalibreKg, kgReales),
    industria: percentage(industriaKg, kgReales),
    podrido: percentage(podridoKg, kgReales),
  };

  percentages.primera = 100 -
    (percentages.segunda + percentages.podrido + percentages.bajoCalibre + percentages.industria);

  const kilosPrimera = kgReales * percentages.primera / 100;
  const kilosClasificados =
    retornoKg +
    otrosCalibresKg +
    verdeKg +
    segundaKg +
    bajoCalibreKg +
    industriaKg +
    podridoKg;

  const clasificacionesConPalots = [
    ['otrosCalibres', 'pOtrosCalibres'],
    ['retorno', 'pRetorno'],
    ['verde', 'pVerde'],
    ['segunda', 'pSegunda'],
    ['bajoCalibre', 'pBajoCalibre'],
    ['industria', 'pIndustria'],
  ];

  const totalPalots = clasificacionesConPalots.reduce(
    (acc, [, palotsField]) => acc + toNumber(entrada[palotsField]),
    toNumber(entrada.crucesPalots) * 4 + paletsEntrada
  );

  const kilosSinClasificar = Math.max(kilosVolcados - kilosClasificados, 0);
  const rendimiento = kilosVolcados > 0 ? (kilosClasificados * 100) / kilosVolcados : 0;

  return {
    kilosVolcados,
    kgReales,
    kilosClasificados,
    kilosSinClasificar,
    rendimiento,
    totalPalots,
    pesoPalotMerma,
    percentages,
    kilosPrimera,
    detalle: {
      retornoKg,
      otrosCalibresKg,
      verdeKg,
      verdeKgParaReales,
      segundaKg,
      bajoCalibreKg,
      industriaKg,
      podridoKg,
    },
  };
};
